import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useFirebase } from '@/contexts/FirebaseContext';
import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { Calendar, Save, Plus, Trash2, Clock } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Calendar as DatePicker } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface UnavailableSlot {
  date: string;  // Format: "YYYY-MM-DD"
  startTime: string;  // Format: "HH:mm" or "all-day"
  endTime: string;  // Format: "HH:mm" or "all-day"
  isFullDay?: boolean;
}

interface Availability {
  instructorEmail: string;
  unavailableSlots: UnavailableSlot[];
}

const timeSlots = [
  "09:00", "09:15", "09:30", "09:45",
  "10:00", "10:15", "10:30", "10:45",
  "11:00", "11:15", "11:30", "11:45",
  "12:00", "12:15", "12:30", "12:45",
  "13:00", "13:15", "13:30", "13:45",
  "14:00", "14:15", "14:30", "14:45",
  "15:00", "15:15", "15:30", "15:45",
  "16:00", "16:15", "16:30", "16:45",
  "17:00", "17:15", "17:30", "17:45"
];

const InstructorAvailability = () => {
  const [availability, setAvailability] = useState<Availability>({
    instructorEmail: '',
    unavailableSlots: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [isFullDay, setIsFullDay] = useState(false);
  const { currentUser, getFirestore } = useFirebase();
  const { toast } = useToast();
  const db = getFirestore();

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!currentUser?.email) {
        console.log('No current user email found');
        return;
      }

      try {
        setLoading(true);
        // Check user role first
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (!userDoc.exists()) {
          console.log('User document not found');
          return;
        }
        const userData = userDoc.data();
        console.log('Current user data:', userData);
        console.log('User role:', userData.role);

        const availabilityDoc = await getDoc(doc(db, 'instructor_availability', currentUser.email));
        
        if (availabilityDoc.exists()) {
          const data = availabilityDoc.data() as Availability;
          if (!data.instructorEmail) {
            data.instructorEmail = currentUser.email;
          }
          console.log('Fetched availability data:', data);
          setAvailability(data);
        } else {
          const defaultAvailability: Availability = {
            instructorEmail: currentUser.email,
            unavailableSlots: []
          };
          console.log('Initializing new availability data:', defaultAvailability);
          setAvailability(defaultAvailability);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        toast({
          title: 'Error',
          description: 'Failed to load availability settings.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [currentUser, toast]);

  const handleAddUnavailableSlot = () => {
    if (!selectedDate) {
      toast({
        title: 'Missing Information',
        description: 'Please select a date.',
        variant: 'destructive',
      });
      return;
    }

    if (!isFullDay && (!startTime || !endTime)) {
      toast({
        title: 'Missing Information',
        description: 'Please select both start and end times or mark as full day.',
        variant: 'destructive',
      });
      return;
    }

    // Validate time range
    if (!isFullDay) {
      const start = parseInt(startTime.split(':')[0]);
      const end = parseInt(endTime.split(':')[0]);
      if (start >= end) {
        toast({
          title: 'Invalid Time Range',
          description: 'End time must be after start time.',
          variant: 'destructive',
        });
        return;
      }
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    
    // Check if this slot overlaps with existing unavailable slots
    const hasOverlap = availability.unavailableSlots.some(slot => {
      if (slot.date !== formattedDate) return false;
      if (slot.isFullDay || isFullDay) return true;
      
      const existingStart = parseInt(slot.startTime.split(':')[0]);
      const existingEnd = parseInt(slot.endTime.split(':')[0]);
      const newStart = parseInt(startTime.split(':')[0]);
      const newEnd = parseInt(endTime.split(':')[0]);
      
      return (
        (newStart >= existingStart && newStart < existingEnd) ||
        (newEnd > existingStart && newEnd <= existingEnd) ||
        (newStart <= existingStart && newEnd >= existingEnd)
      );
    });

    if (hasOverlap) {
      toast({
        title: 'Time Slot Overlap',
        description: 'This time range overlaps with an existing unavailable slot.',
        variant: 'destructive',
      });
      return;
    }

    setAvailability(prev => ({
      instructorEmail: prev.instructorEmail,
      unavailableSlots: [
        ...prev.unavailableSlots,
        {
          date: formattedDate,
          startTime: isFullDay ? 'all-day' : startTime,
          endTime: isFullDay ? 'all-day' : endTime,
          isFullDay: isFullDay
        }
      ]
    }));

    // Reset selections
    setSelectedDate(undefined);
    setStartTime("");
    setEndTime("");
    setIsFullDay(false);
  };

  const handleRemoveUnavailableSlot = (date: string, startTime: string, endTime: string, isFullDay: boolean) => {
    setAvailability(prev => ({
      instructorEmail: prev.instructorEmail,
      unavailableSlots: prev.unavailableSlots.filter(
        slot => !(slot.date === date && 
                 slot.startTime === startTime && 
                 slot.endTime === endTime && 
                 slot.isFullDay === isFullDay)
      )
    }));
  };

  const handleSave = async () => {
    if (!currentUser?.uid || !currentUser?.email) {
      console.log('No current user:', currentUser);
      toast({
        title: 'Error',
        description: 'You must be logged in to save availability.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      
      // Get the user's role from Firestore
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (!userDoc.exists()) {
        console.log('User document not found for ID:', currentUser.uid);
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      console.log('Current Firebase user:', currentUser);
      console.log('User data from Firestore:', userData);
      console.log('Checking role:', userData.role);
      console.log('Checking email match:', userData.email, currentUser.email);
      
      if (userData.role !== 'instructor') {
        console.log('User role is not instructor:', userData.role);
        throw new Error('Only instructors can set availability');
      }

      // Verify email matches
      if (userData.email !== currentUser.email) {
        console.log('Email mismatch:', userData.email, currentUser.email);
        throw new Error('Email verification failed');
      }

      // Validate and clean the unavailable slots
      const cleanedUnavailableSlots = availability.unavailableSlots.map(slot => ({
        date: slot.date || '',
        startTime: slot.startTime || 'all-day',
        endTime: slot.endTime || 'all-day',
        isFullDay: Boolean(slot.isFullDay)
      }));

      // Create a clean data structure that matches what the rules expect
      const availabilityData = {
        instructorEmail: userData.email || '',
        instructorId: currentUser.uid || '',
        instructorRole: userData.role || 'instructor',
        unavailableSlots: cleanedUnavailableSlots,
        updatedAt: new Date().toISOString(),
        metadata: {
          lastModified: {
            timestamp: new Date().toISOString(),
            userId: currentUser.uid || '',
            userEmail: userData.email || ''
          }
        }
      };
      
      console.log('Attempting to save availability data:', availabilityData);
      console.log('To document path:', `instructor_availability/${userData.email}`);
      
      // First try to get the existing document
      const availabilityRef = doc(db, 'instructor_availability', userData.email);
      const existingDoc = await getDoc(availabilityRef);
      
      if (existingDoc.exists()) {
        // Update existing document
        await setDoc(availabilityRef, {
          ...availabilityData,
          createdAt: existingDoc.data().createdAt || new Date().toISOString() // Preserve creation date
        }, { merge: true });
      } else {
        // Create new document
        await setDoc(availabilityRef, {
          ...availabilityData,
          createdAt: new Date().toISOString()
        });
      }

      toast({
        title: 'Success',
        description: 'Availability settings saved successfully.',
      });
    } catch (error) {
      console.error('Error saving availability:', error);
      let errorMessage = 'Failed to save availability settings.';
      
      if (error instanceof Error) {
        // Provide more specific error messages
        switch(error.message) {
          case 'User document not found':
            errorMessage = 'Your user profile was not found. Please try logging out and back in.';
            break;
          case 'Only instructors can set availability':
            errorMessage = 'You must be an instructor to set availability.';
            break;
          case 'Email verification failed':
            errorMessage = 'Email verification failed. Please try logging out and back in.';
            break;
          case 'Missing or insufficient permissions':
            errorMessage = 'You do not have permission to update availability. Please verify you are logged in as an instructor.';
            break;
          default:
            if (error.message.includes('permission')) {
              errorMessage = 'Permission denied. Please verify you are logged in as an instructor.';
            }
        }
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="instructor">
        <div className="flex items-center justify-center h-64">
          <p>Loading availability settings...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Manage Availability</h1>
          <p className="text-gray-600 mt-2">Mark specific dates and times when you are unavailable</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Unavailable Time Slots
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Select Date</label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={setSelectedDate}
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Checkbox
                      id="fullDay"
                      checked={isFullDay}
                      onCheckedChange={(checked) => {
                        setIsFullDay(checked as boolean);
                        if (checked) {
                          setStartTime("");
                          setEndTime("");
                        }
                      }}
                    />
                    <Label htmlFor="fullDay">Mark as full day unavailable</Label>
                  </div>
                  {!isFullDay && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Time</Label>
                        <Select value={startTime} onValueChange={setStartTime}>
                          <SelectTrigger>
                            <SelectValue placeholder="From" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <Select value={endTime} onValueChange={setEndTime}>
                          <SelectTrigger>
                            <SelectValue placeholder="To" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleAddUnavailableSlot}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Unavailable Slot
                </Button>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Current Unavailable Slots</h3>
                {availability.unavailableSlots.length === 0 ? (
                  <p className="text-gray-500">No unavailable slots set</p>
                ) : (
                  <div className="grid gap-4">
                    {availability.unavailableSlots.map((slot, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Calendar className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="font-medium">
                              {format(new Date(slot.date), 'EEEE, MMMM d, yyyy')}
                            </p>
                            {slot.isFullDay ? (
                              <p className="text-sm text-red-500">Full Day Unavailable</p>
                            ) : (
                              <p className="text-sm text-gray-500">
                                {slot.startTime} - {slot.endTime}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUnavailableSlot(slot.date, slot.startTime, slot.endTime, slot.isFullDay || false)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InstructorAvailability; 